import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact daily win/loss indicator chip for the landing page hero.
 * Pulls from /api/performance/public (cached 60s on the server).
 */
export default function LiveDailyChip() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/performance/public`);
        if (mounted) setData(r.data);
      } catch (err) {
        // network/offline — chip stays hidden
        console.debug("[live-chip] fetch failed", err);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  if (!data?.today) {
    return (
      <div
        data-testid="landing-daily-chip-skeleton"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-[#0F111A]/60 text-slate-500 font-mono text-[10px] uppercase tracking-[0.3em]"
      >
        <Activity className="w-3.5 h-3.5" /> loading today's edge…
      </div>
    );
  }

  const t = data.today;
  const wr = t.win_rate_pct || 0;
  const wins = t.wins || 0;
  const losses = t.losses || 0;
  const tone =
    wr >= 70 ? "text-emerald-300 border-emerald-400/40 bg-emerald-500/10"
    : wr >= 50 ? "text-amber-300 border-amber-400/40 bg-amber-500/10"
    : "text-rose-300 border-rose-400/40 bg-rose-500/10";

  return (
    <Link
      to="/performance"
      data-testid="landing-daily-chip"
      className={`inline-flex items-center gap-3 px-4 py-2 rounded-full border backdrop-blur-sm transition hover:scale-[1.02] ${tone}`}
      title={`Today across demo + live trading — ${wins}W / ${losses}L`}
    >
      <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em]">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-current" />
        </span>
        today demo + live
      </span>
      <span className="h-4 w-px bg-current opacity-30" />
      <span className="font-display font-extrabold text-lg leading-none" data-testid="landing-daily-chip-winrate">
        {wr.toFixed(0)}%
      </span>
      <span className="font-mono text-[10px] opacity-80" data-testid="landing-daily-chip-wl">
        {wins}W&nbsp;·&nbsp;{losses}L
      </span>
    </Link>
  );
}
